import React from 'react';
import { connect } from 'react-redux';
import Modal from 'components/common/Modal/Modal';
import moment from 'moment';
import lodash from 'lodash';

import { addExternalUser } from 'binder/actions/externalUsers';
import { getTimezoneOffset } from 'common/selectors/index';

import { Button, BUTTON } from 'components/common/Button/Button';
const { SIZE, COLOR, BGCOLOR } = BUTTON;
import styles from './AddExternalUserModal.scss';
import DateRangePicker from 'components/common/DateRangePicker/DateRangePicker';
import Input from 'components/common/Input/FormInput';
import { getHoursMinutesForCurrentTime, getHoursMinutesFromDate, applyTimeZoneToDate } from 'common/util/dateTime';
import { binderInstitutionIdSelector } from 'common/selectors';
import * as dateTimeUtils from 'common/util/dateTime';

const ADD_NEW_USER_DESCRIPTION =
    `This person does not have an account in Complion yet.
Please complete the required information and we will create an account
and send out a welcome email to this person.
`;

const ADD_EXISTING_USER_DESCRIPTION =
    `This person already has an account in Complion.
To provide access to this binder, please complete the Access Window and then
add this user.
`;

const EDIT_EXISTING_USER_DESCRIPTION = 'Changes you make to the Access Window will take effect immediately.';

const ExternalUserDescription = ({ description }) => {
    return <p className={styles.descriptionText}>{description}</p>;
};

class AddExternalUserModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            firstName: lodash.get(this.props, 'user.firstName', ''),
            lastName: lodash.get(this.props, 'user.lastName', ''),
            accessStartDate: this.props.accessStartDate,
            accessEndDate: this.props.accessEndDate,
            accessStartTime: this.props.accessStartTime,
            accessEndTime: this.props.accessEndTime,
            loading: false,
        };
        this.close = this.close.bind(this);
        this.updateState = this.updateState.bind(this);
        this.missingRequiredFields = this.missingRequiredFields.bind(this);
        this.addUser = this.addUser.bind(this);
        this.onDateChange = this.onDateChange.bind(this);
        this.isRequiredFieldComplete = this.isRequiredFieldComplete.bind(this);
    }

    componentDidMount() {
        document.body.style['overflow-y'] = 'hidden';
    }

    componentWillUnmount() {
        document.body.style['overflow-y'] = 'visible';
    }

    renderDescription() {
        const editingExistingUser = this.props.user.binderExpiration
            && this.props.user.binderExpiration.startDate
            && this.props.user.binderExpiration.endDate;

        const addingNewUser = !this.props.user.firstName
            && !this.props.user.lastName;

        const addingExistingUser = this.props.user.firstName
            && this.props.user.lastName;

        if (editingExistingUser) {
            return <ExternalUserDescription description={EDIT_EXISTING_USER_DESCRIPTION} />;
        } else if (addingNewUser) {
            return <ExternalUserDescription description={ADD_NEW_USER_DESCRIPTION} />;
        } else if (addingExistingUser) {
            return <ExternalUserDescription description={ADD_EXISTING_USER_DESCRIPTION} />;
        }
    }

    close() {
        this.props.onClose();
    }

    updateState(type, event) {
        this.setState({ [type]: event.target.value });
    }

    isRequiredFieldComplete(fieldName) {
        const value = this.state[fieldName];
        return Boolean(value);
    }

    onDateChange(startDate, endDate) {
        this.setState({
            accessStartDate: startDate ? moment(startDate) : null,
            accessEndDate: endDate ? moment(endDate) : null,
        });
    }

    addUser() {
        if (!this.missingRequiredFields()) {
            this.setState({
                loading: true,
            });

            const firstName = this.props.user.firstName || this.state.firstName;
            const lastName = this.props.user.lastName || this.state.lastName;
            const binderId = lodash.get(this.props, 'binder.id');
            const institutionId = binderInstitutionIdSelector(this.props);
            const normalizedEmailAddress = normalizeEmail(this.props.user.emailAddress);
            const user = {
                firstName: firstName,
                lastName: lastName,
                displayName: `${firstName} ${lastName}`,
                emailAddress: normalizedEmailAddress,
            };

            const start = dateTimeUtils.formatDateAndTimeForSave(this.state.accessStartDate,
                this.state.accessStartTime, this.props.timezoneOffset);
            const end = dateTimeUtils.formatDateAndTimeForSave(this.state.accessEndDate,
                this.state.accessEndTime, this.props.timezoneOffset);

            this.props.addExternalUser(user, binderId, institutionId, start, end);
            this.props.onSubmit();
        }
    }

    hasFirstName() {
        const firstNameFromProps = lodash.get(this.props, 'user.firstName');
        const firstNameFromState = lodash.get(this.state, 'firstName');
        return firstNameFromProps || firstNameFromState;
    }

    hasLastName() {
        const lastNameFromProps = lodash.get(this.props, 'user.lastName');
        const lastNameFromState = lodash.get(this.state, 'lastName');
        return lastNameFromProps || lastNameFromState;
    }

    missingRequiredFields() {
        return !this.hasFirstName()
            || !this.hasLastName()
            || !this.state.accessStartDate
            || !this.state.accessEndDate
            || !this.state.accessStartTime
            || !this.state.accessEndTime;
    }

    renderAccountInformation() {
        return (
            <div className={styles.section} id="add-external-user-account-information">
                <h3 className={styles.title}>Account Information</h3>
                <ExternalUserEmailLabel emailAddress={this.props.user.emailAddress} />
                {this.renderFirstLastName()}
            </div>
        );
    }

    renderFirstLastName() {
        const { firstName, lastName } = this.props.user;
        const existingUser = firstName && lastName;

        if (existingUser) {
            return (
                <ExternalUserFirstLastNameLabels
                    firstName={firstName}
                    lastName={lastName}
                />
            );
        }

        return (
            <ExternalUserFirstLastNameInputs
                firstName={this.state.firstName}
                lastName={this.state.lastName}
                onClick={this.addUser}
                onChange={this.updateState}
                isRequiredFieldComplete={this.isRequiredFieldComplete}
            />
        );
    }

    renderAccessWindow() {
        return (
            <div id="add-external-user-access-window-information" data-cy="AddExternalUserModal">
                <h3 className={styles.title}>Access Window</h3>
                <p className={styles.descriptionText}>Please select when you would like this monitor to have access to this binder.</p>

                <DateRangeSection start={this.state.accessStartDate} end={this.state.accessEndDate} onDateChange={this.onDateChange} />

                <div id="add-external-user-access-window-time" className={styles.row}>
                    <Input
                        title={'Start Time'}
                        value={this.state.accessStartTime}
                        type={'time'}
                        required={true}
                        isValid={() => this.isRequiredFieldComplete('accessStartTime')}
                        onBlur={(e) => this.updateState('accessStartTime', e)}
                        onChange={(e) => this.updateState('accessStartTime', e)}
                        onClick={this.addUser}
                        data-cy="accessStartTime"
                    />

                    <Input
                        title={'End Time'}
                        value={this.state.accessEndTime}
                        type={'time'}
                        required={true}
                        isValid={() => this.isRequiredFieldComplete('accessEndTime')}
                        onBlur={(e) => this.updateState('accessEndTime', e)}
                        onChange={(e) => this.updateState('accessEndTime', e)}
                        onClick={this.addUser}
                        data-cy="accessEndTime"
                    />
                </div>
            </div>
        );
    }

    renderActionButtons() {
        const isExistingUser = !lodash.isNil(this.props.externalUser);
        const buttonText = isExistingUser ? 'Save Changes' : 'Add User';

        return (
            <div name="add-external-user-action-buttons" className={styles.actionButtons} data-cy="AddExternalUserModal-action">
                <Button
                    text={'Close'}
                    type={SIZE.PRIMARY}
                    color={COLOR.GREY}
                    bgcolor={BGCOLOR.WHITE}
                    onClick={() => this.close()}
                    data-cy="AddExternalUserModal-Close"
                />

                <Button
                    text={this.state.loading ? 'Saving...' : buttonText}
                    type={SIZE.PRIMARY}
                    color={COLOR.WHITE}
                    bgcolor={this.missingRequiredFields() ? BGCOLOR.LIGHTGREY : BGCOLOR.ORANGE}
                    disabled={this.missingRequiredFields() || this.state.loading}
                    onClick={this.addUser}
                    data-cy="AddExternalUserModal-addUser"
                />
            </div>
        );
    }

    render() {
        if (!this.props.user) {
            return null;
        }

        const modalTitle = this.props.user.binderExpiration ? 'Edit Access Window' : 'Add External User';
        return (
            <Modal
                title={modalTitle}
                open={true}
                onClose={this.close}
            >
                <div id="add-external-user-modal-body" className={styles.modalBody} data-cy="AddExternalUserModal-full">
                    {this.renderDescription()}
                    {this.renderAccountInformation()}
                    {this.renderAccessWindow()}
                    {this.renderActionButtons()}
                </div>
            </Modal>
        );
    }

}

const ExternalUserEmailLabel = ({ emailAddress }) => {
    return (
        <div id="add-external-user-email" className={styles.labelValuePair} data-cy="AddExternalUserModal-addModal">
            <label className={styles.headerTitle} htmlFor="userEmail">Email Address</label>
            <label id="userEmail" className={styles.value}>{normalizeEmail(emailAddress)}</label>
        </div>
    );
};

const ExternalUserNameLabel = ({ namePart, label, name }) => {
    return (
        <div id={`add-external-user-${namePart}`} className={styles.labelValuePairSplit} data-cy={`AddExternalUserModal-${namePart}`}>
            <label className={styles.headerTitle}>{label}</label>
            <label className={styles.value}>{name}</label>
        </div>
    );
};

const ExternalUserFirstLastNameLabels = ({ firstName, lastName }) => {
    return (
        <div id="add-external-user-first-last-name" className={styles.row} data-cy="AddExternalUserModal-fullName">
            <ExternalUserNameLabel
                namePart={'firstName'}
                label={'First Name'}
                name={firstName}
            />
            <ExternalUserNameLabel
                namePart={'lastName'}
                label={'Last Name'}
                name={lastName}
            />
        </div>
    );
};

const ExternalUserFirstLastNameInputs = ({ firstName, lastName, onClick, onChange, isRequiredFieldComplete }) => {
    return (
        <div id="add-external-user-first-last-name" className={styles.row} data-cy="AddExternalUserModal-firstLastName">
            <ExternalUserNameInput
                name={firstName}
                namePart={'firstName'}
                label={'First Name'}
                placeholder={'John'}
                onClick={onClick}
                onChange={onChange}
                isValid={() => isRequiredFieldComplete('firstName')}
                autoFocus={true}
            />

            <ExternalUserNameInput
                name={lastName}
                namePart={'lastName'}
                label={'Last Name'}
                placeholder={'Doe'}
                onClick={onClick}
                onChange={onChange}
                isValid={() => isRequiredFieldComplete('lastName')}
            />
        </div>
    );
};

const ExternalUserNameInput = ({ namePart, label, name, placeholder, autoFocus, onClick, onChange, isValid }) => {
    return (
        <Input
            title={label}
            value={name}
            placeholder={placeholder}
            required={true}
            isValid={isValid}
            onBlur={(e) => onChange(namePart, e)}
            onChange={(e) => onChange(namePart, e)}
            errorText={'Please fill in this field'}
            autoFocus={autoFocus}
            onClick={onClick}
            data-cy="AddExternalUserModal-NameInput"
        />
    );
};

const DateRangeSection = ({ start, end, onDateChange }) => {
    return (
        <div className={styles.section} data-cy="AddExternalUserModal-DateRangeSection">
            <div className={styles.header}>
                <label className={styles.requiredHeaderTitle}>Date Range</label>
                <span className={styles.required}>Required</span>
            </div>
            <DateRangePicker startDate={start} endDate={end} onChange={onDateChange} />
        </div>
    );
};

function normalizeEmail(emailAddress) {
    if (emailAddress && lodash.isString(emailAddress)) {
        return emailAddress.toLowerCase();
    }
}

export default connect((state, props) => {
    const binder = lodash.get(state, 'binder', {});
    const externalUser = lodash.get(props, 'externalUser', null);
    const binderExpiration = lodash.get(props, 'externalUser.binderExpiration', null);
    const timezoneOffset = getTimezoneOffset(state);

    let accessStartDate = null;
    let accessEndDate = null;
    let accessStartTime, accessEndTime;
    if (binderExpiration) {
        accessStartDate = applyTimeZoneToDate(binderExpiration.startDate, timezoneOffset);
        accessEndDate = applyTimeZoneToDate(binderExpiration.endDate, timezoneOffset);

        accessStartTime = getHoursMinutesFromDate(accessStartDate);
        accessEndTime = getHoursMinutesFromDate(accessEndDate);
    } else {
        accessStartTime = getHoursMinutesForCurrentTime();
        accessEndTime = getHoursMinutesForCurrentTime();
    }

    return {
        binder,
        user: externalUser,
        accessStartDate,
        accessEndDate,
        accessStartTime,
        accessEndTime,
        timezoneOffset,
    };
}, {
    addExternalUser,
})(AddExternalUserModal);
